import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Play, Pause, CheckCircle, XCircle, Mic, Radio,
         RefreshCw, Volume2, Clock, FileText, Zap, ChevronDown, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE } from '../services/apiBase';

const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';
const API    = API_BASE;
const MEDIA  = 'https://software.radiostream.co.za/media/audio';

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ─── Content types ────────────────────────────────────────────────────────────
const CONTENT_TYPES = [
  { id: 'dj_clip',   label: 'DJ Clip',   icon: Mic,      desc: 'Short energetic DJ drops & shoutouts',   color: '#2f81f7' },
  { id: 'ai_advert', label: 'AI Advert', icon: Radio,    desc: 'AI-voiced advertisement for a product',  color: '#8b5cf6' },
  { id: 'ai_liner',  label: 'Liner',     icon: Zap,      desc: 'Station ID, ident or sweeper liner',     color: '#2dd4bf' },
];

const STATUS_CFG = {
  draft:              { label: 'Draft',          color: '#6b7280' },
  generating:         { label: 'Generating…',    color: '#f59e0b' },
  pending_review:     { label: 'Pending Review', color: '#8b5cf6' },
  approved:           { label: 'Approved',       color: '#22c55e' },
  active:             { label: 'Active',         color: '#2f81f7' },
  rejected:           { label: 'Rejected',       color: '#ef4444' },
  archived:           { label: 'Archived',       color: '#6b7280' },
};

// ─── Mini audio player ────────────────────────────────────────────────────────
function MiniPlayer({ url, accentColor = '#2f81f7' }) {
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const rafRef   = useRef(null);

  const tick = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    }
  };

  const toggle = () => {
    if (state === 'loading') return;
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onloadedmetadata = () => setDuration(audioRef.current.duration);
      audioRef.current.onended = () => { setState('idle'); setProgress(0); cancelAnimationFrame(rafRef.current); };
      audioRef.current.onerror = () => { setState('error'); toast.error('Could not load audio'); };
    }
    if (state === 'playing') {
      audioRef.current.pause();
      setState('idle');
      cancelAnimationFrame(rafRef.current);
    } else {
      setState('loading');
      audioRef.current.play()
        .then(() => { setState('playing'); rafRef.current = requestAnimationFrame(tick); })
        .catch(() => setState('error'));
    }
  };

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, [url]);

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button onClick={toggle} style={{
        width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: state === 'error' ? '#ef444422' : accentColor + '22',
        color: state === 'error' ? '#ef4444' : accentColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {state === 'loading'
          ? <div style={{ width: 10, height: 10, border: `2px solid ${accentColor}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          : state === 'playing' ? <Pause size={13} /> : <Play size={13} />}
      </button>

      {/* Waveform-style progress bar */}
      <div style={{ flex: 1, position: 'relative', height: 28, cursor: 'pointer' }}
        onClick={e => {
          if (!audioRef.current || !duration) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const ratio = (e.clientX - rect.left) / rect.width;
          audioRef.current.currentTime = ratio * duration;
          setProgress(audioRef.current.currentTime);
        }}>
        {/* Fake waveform bars */}
        <div style={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
          {Array.from({ length: 40 }, (_, i) => {
            const h = 20 + Math.sin(i * 0.7) * 10 + Math.cos(i * 1.3) * 6;
            const filled = (i / 40) * 100 < pct;
            return (
              <div key={i} style={{
                flex: 1, height: `${h}%`, borderRadius: 1,
                background: filled ? accentColor : accentColor + '33',
                transition: 'background 0.1s',
              }} />
            );
          })}
        </div>
      </div>

      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>
        {duration > 0 ? `${Math.floor(duration)}s` : '—'}
      </span>
    </div>
  );
}

// ─── Voice card ───────────────────────────────────────────────────────────────
function VoiceCard({ voice, selected, onClick }) {
  const genderColor = voice.gender === 'female' ? '#f472b6' : voice.gender === 'male' ? '#2f81f7' : '#6b7280';
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const playSample = (e) => {
    e.stopPropagation();
    if (!voice.sample_url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(voice.sample_url);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); audioRef.current.currentTime = 0; setPlaying(false); }
    else { audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
      border: `1px solid ${selected ? '#2f81f7' : 'var(--border)'}`,
      background: selected ? '#2f81f718' : 'var(--bg-elevated)',
      transition: 'all 0.12s', display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: genderColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Mic size={16} color={genderColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{voice.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
          <span style={{ color: genderColor, fontWeight: 600, textTransform: 'capitalize' }}>{voice.gender || 'Neutral'}</span>
          <span>·</span>
          <span>{voice.provider}</span>
        </div>
      </div>
      {voice.sample_url && (
        <button onClick={playSample} style={{
          width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: playing ? '#22c55e22' : 'transparent',
          color: playing ? '#22c55e' : 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {playing ? <Pause size={11} /> : <Volume2 size={11} />}
        </button>
      )}
      {selected && <CheckCircle size={15} color="#2f81f7" />}
    </div>
  );
}

// ─── Generated result card ────────────────────────────────────────────────────
function ResultCard({ item, voices, onApprove, onActivate, onReject, onRefresh }) {
  const cfg = STATUS_CFG[item.status] || { label: item.status, color: '#6b7280' };
  const voiceName = voices.find(v => v.id === item.voice_id)?.name || '—';
  const audioUrl = `${MEDIA}/${item.id}.mp3`;
  const ctCfg = CONTENT_TYPES.find(c => c.id === item.content_type) || CONTENT_TYPES[0];

  return (
    <div style={{
      background: 'var(--bg-elevated)', borderRadius: 'var(--radius)',
      border: `1px solid ${ctCfg.color}33`, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', background: ctCfg.color + '0e', borderBottom: `1px solid ${ctCfg.color}22`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <ctCfg.icon size={15} color={ctCfg.color} />
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{item.name}</span>
        <span style={{
          fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
          background: cfg.color + '22', color: cfg.color,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {item.status === 'generating' && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, animation: 'pulse 1s infinite' }} />
          )}
          {cfg.label}
        </span>
      </div>

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Script */}
        {item.script_text && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FileText size={11} /> Script
            </div>
            <div style={{
              background: 'var(--bg-surface)', borderRadius: 8, padding: '12px 14px',
              fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)',
              fontStyle: 'italic', borderLeft: `3px solid ${ctCfg.color}`,
            }}>
              "{item.script_text}"
            </div>
          </div>
        )}

        {/* Metadata chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { icon: Mic,   value: voiceName },
            item.duration_seconds && { icon: Clock, value: `${Math.round(item.duration_seconds)}s` },
            item.loudness_lufs   && { icon: Volume2, value: `${item.loudness_lufs?.toFixed(1)} LUFS` },
          ].filter(Boolean).map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: 'var(--bg-surface)', fontSize: 12, color: 'var(--text-secondary)' }}>
              <m.icon size={11} color="var(--text-muted)" />{m.value}
            </div>
          ))}
        </div>

        {/* Audio player */}
        {item.asset_url || item.status === 'approved' || item.status === 'active' ? (
          <MiniPlayer url={audioUrl} accentColor={ctCfg.color} />
        ) : item.status === 'generating' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', color: 'var(--text-muted)', fontSize: 12 }}>
            <div style={{ width: 16, height: 16, border: '2px solid #f59e0b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            Generating audio with AI…
          </div>
        ) : null}

        {/* Action buttons */}
        {(item.status === 'draft' || item.status === 'generating') ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onRefresh}><RefreshCw size={12} /> Refresh</button>
          </div>
        ) : item.status === 'pending_review' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onApprove} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid #22c55e44', background: '#22c55e18', color: '#22c55e', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
              <CheckCircle size={13} /> Approve
            </button>
            <button onClick={onReject} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid #ef444444', background: '#ef444418', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
              <XCircle size={13} /> Reject
            </button>
          </div>
        ) : item.status === 'approved' ? (
          <button onClick={onActivate} style={{ width: '100%', padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid #2f81f744', background: '#2f81f718', color: '#2f81f7', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <Zap size={13} /> Activate (go live)
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AIContent() {
  const [voices, setVoices]       = useState([]);
  const [languages, setLanguages] = useState([]);
  const [content, setContent]     = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);

  // Studio form
  const [contentType, setContentType] = useState('dj_clip');
  const [name,        setName]        = useState('');
  const [voiceId,     setVoiceId]     = useState('');
  const [langId,      setLangId]      = useState('');
  const [prompt,      setPrompt]      = useState('');
  const [genderFilter, setGenderFilter] = useState('all');

  // Last generated
  const [result, setResult] = useState(null);

  // Library filter
  const [libFilter, setLibFilter]   = useState('all');
  const [libType,   setLibType]     = useState('all');
  const [libSearch, setLibSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [vRes, lRes, cRes, tRes] = await Promise.allSettled([
      axios.get(`${API}/ai/voices`, { headers: authHeaders() }),
      axios.get(`${API}/ai/languages`, { headers: authHeaders() }),
      axios.get(`${API}/ai/tenants/${TENANT}/content`, { headers: authHeaders() }),
      axios.get(`${API}/ai/tenants/${TENANT}/prompt-templates`, { headers: authHeaders() }),
    ]);

    const voices_data    = vRes.status === 'fulfilled' ? (vRes.value.data || []) : [];
    const languages_data = lRes.status === 'fulfilled' ? (lRes.value.data || []) : [];
    const content_data   = cRes.status === 'fulfilled' ? (cRes.value.data || []) : [];
    const templates_data = tRes.status === 'fulfilled' ? (tRes.value.data || []) : [];

    // Log failures for debugging
    [{ r: vRes, n: 'voices' }, { r: lRes, n: 'languages' }, { r: cRes, n: 'content' }, { r: tRes, n: 'templates' }]
      .filter(x => x.r.status === 'rejected')
      .forEach(x => console.warn(`AI Studio: failed to load ${x.n}`, x.r.reason?.response?.data || x.r.reason?.message));

    setVoices(voices_data);
    setLanguages(languages_data);
    setContent([...content_data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    setTemplates(templates_data);
    if (!langId  && languages_data.length) setLangId(languages_data[0].id);
    if (!voiceId && voices_data.length)    setVoiceId(voices_data[0].id);

    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!name.trim()) { toast.error('Give this clip a name'); return; }
    if (!voiceId)     { toast.error('Select a voice'); return; }
    if (!langId)      { toast.error('Select a language'); return; }
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/tenants/${TENANT}/generate`, {
        content_type: contentType,
        name: name.trim(),
        voice_id: voiceId,
        language_id: langId,
        input_text: prompt.trim(),
      }, { headers: authHeaders() });
      toast.success('Generation started!');
      setResult(res.data);
      setContent(prev => [res.data, ...prev]);
      setName('');
      setPrompt('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const refreshResult = async () => {
    if (!result) return;
    try {
      const res = await axios.get(`${API}/ai/tenants/${TENANT}/content`, { headers: authHeaders() });
      const fresh = (res.data || []).find(c => c.id === result.id);
      if (fresh) { setResult(fresh); setContent(prev => prev.map(c => c.id === fresh.id ? fresh : c)); }
    } catch { toast.error('Refresh failed'); }
  };

  const handleApprove = async (id) => {
    try {
      const res = await axios.post(`${API}/ai/content/${id}/review`, { approved: true }, { headers: authHeaders() });
      toast.success('Approved');
      if (result?.id === id) setResult(res.data);
      setContent(prev => prev.map(c => c.id === id ? res.data : c));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    try {
      const res = await axios.post(`${API}/ai/content/${id}/review`, { approved: false, rejection_reason: reason || null }, { headers: authHeaders() });
      toast.success('Rejected');
      if (result?.id === id) setResult(res.data);
      setContent(prev => prev.map(c => c.id === id ? res.data : c));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleActivate = async (id) => {
    try {
      const res = await axios.post(`${API}/ai/content/${id}/activate`, {}, { headers: authHeaders() });
      toast.success('Now live!');
      if (result?.id === id) setResult(res.data);
      setContent(prev => prev.map(c => c.id === id ? res.data : c));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Filtered voices for the voice grid
  const filteredVoices = voices.filter(v => {
    if (genderFilter !== 'all' && v.gender !== genderFilter) return false;
    return true;
  });

  // Library
  let libItems = [...content];
  if (libFilter !== 'all') libItems = libItems.filter(c => c.status === libFilter);
  if (libType   !== 'all') libItems = libItems.filter(c => c.content_type === libType);
  if (libSearch)           libItems = libItems.filter(c => c.name.toLowerCase().includes(libSearch.toLowerCase()));

  const ctCfg = CONTENT_TYPES.find(c => c.id === contentType) || CONTENT_TYPES[0];

  // Stats
  const active  = content.filter(c => c.status === 'active').length;
  const pending = content.filter(c => c.status === 'pending_review').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #2f81f7, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={18} color="white" />
            </div>
            AI Studio
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>Generate voice content with AI — DJ clips, adverts, liners</div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Total', value: content.length, color: 'var(--text-muted)' },
            { label: 'Pending', value: pending, color: '#8b5cf6' },
            { label: 'Active', value: active, color: '#22c55e' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Studio ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, marginBottom: 28 }}>

        {/* Left: Generator */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Content type selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {CONTENT_TYPES.map(ct => (
              <button key={ct.id} onClick={() => setContentType(ct.id)} style={{
                padding: '14px 16px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                border: `1px solid ${contentType === ct.id ? ct.color : 'var(--border)'}`,
                background: contentType === ct.id ? ct.color + '18' : 'var(--bg-surface)',
                textAlign: 'left', transition: 'all 0.12s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <ct.icon size={15} color={contentType === ct.id ? ct.color : 'var(--text-muted)'} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: contentType === ct.id ? ct.color : 'var(--text-primary)' }}>{ct.label}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ct.desc}</div>
              </button>
            ))}
          </div>

          {/* Prompt area */}
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: `1px solid ${ctCfg.color}33`, overflow: 'hidden' }}>
            {/* Gradient header bar */}
            <div style={{
              padding: '12px 16px', background: `linear-gradient(90deg, ${ctCfg.color}22, transparent)`,
              borderBottom: `1px solid ${ctCfg.color}22`, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Wand2 size={14} color={ctCfg.color} />
              <span style={{ fontSize: 12, fontWeight: 700, color: ctCfg.color }}>Studio Prompt</span>
              {templates.filter(t => t.content_type === contentType).length > 0 && (
                <select
                  className="form-select"
                  style={{ marginLeft: 'auto', height: 28, fontSize: 11, width: 180, padding: '0 8px' }}
                  onChange={e => { if (e.target.value) setPrompt(e.target.value); }}
                  defaultValue="">
                  <option value="">Load template…</option>
                  {templates.filter(t => t.content_type === contentType).map(t => (
                    <option key={t.id} value={t.system_prompt}>{t.name || t.content_type}</option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Clip Name</label>
                <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder={`e.g. Weekend Vibes ${ctCfg.label}`} />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Prompt / Script Direction</label>
                <textarea
                  className="form-input"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={
                    contentType === 'dj_clip'   ? "e.g. Hey it's Friday evening, we've got the best hits all night long, keep it locked in..." :
                    contentType === 'ai_advert' ? "e.g. Introducing our summer sale — 30% off everything this weekend only..." :
                    "e.g. You're listening to InStore Radio, the sound of [brand name]..."
                  }
                  rows={5}
                  style={{ resize: 'vertical', lineHeight: 1.6, fontFamily: 'inherit' }}
                />
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  alignSelf: 'flex-end', padding: '10px 24px', borderRadius: 'var(--radius-sm)', border: 'none',
                  cursor: generating ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14,
                  background: generating
                    ? 'var(--bg-elevated)'
                    : `linear-gradient(135deg, ${ctCfg.color}, ${ctCfg.color}cc)`,
                  color: generating ? 'var(--text-muted)' : 'white',
                  display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: generating ? 'none' : `0 4px 16px ${ctCfg.color}44`,
                  transition: 'all 0.15s',
                }}>
                {generating
                  ? <><div style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Generating…</>
                  : <><Sparkles size={14} /> Generate {ctCfg.label}</>
                }
              </button>
            </div>
          </div>
        </div>

        {/* Right: Voice selector */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Voice</div>

            {/* Language selector */}
            <select className="form-select" style={{ marginBottom: 10, height: 34, fontSize: 12 }}
              value={langId} onChange={e => setLangId(e.target.value)}>
              <option value="">Select language…</option>
              {languages.map(l => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
            </select>

            {/* Gender filter */}
            <div style={{ display: 'flex', gap: 4 }}>
              {['all', 'male', 'female', 'neutral'].map(g => (
                <button key={g} onClick={() => setGenderFilter(g)} style={{
                  flex: 1, padding: '5px 0', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  background: genderFilter === g ? 'var(--accent)' : 'var(--bg-elevated)',
                  color: genderFilter === g ? 'white' : 'var(--text-muted)',
                  textTransform: 'capitalize',
                }}>{g}</button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading voices…</div>
            ) : filteredVoices.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No voices for this filter</div>
            ) : filteredVoices.map(v => (
              <VoiceCard key={v.id} voice={v} selected={voiceId === v.id} onClick={() => setVoiceId(v.id)} />
            ))}
          </div>
        </div>
      </div>

      {/* Result card */}
      {result && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={12} /> Latest Generation
            <button className="btn btn-ghost btn-sm" onClick={() => setResult(null)} style={{ marginLeft: 'auto', fontSize: 11 }}>Dismiss</button>
          </div>
          <ResultCard
            item={result}
            voices={voices}
            onApprove={() => handleApprove(result.id)}
            onReject={() => handleReject(result.id)}
            onActivate={() => handleActivate(result.id)}
            onRefresh={refreshResult}
          />
        </div>
      )}

      {/* ── Library ── */}
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        {/* Library header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Content Library</span>
          <input className="form-input" style={{ width: 200, height: 32 }}
            placeholder="Search…" value={libSearch} onChange={e => setLibSearch(e.target.value)} />
          <select className="form-select" style={{ height: 32, width: 140, fontSize: 12 }}
            value={libType} onChange={e => setLibType(e.target.value)}>
            <option value="all">All types</option>
            {CONTENT_TYPES.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
          </select>
          <select className="form-select" style={{ height: 32, width: 160, fontSize: 12 }}
            value={libFilter} onChange={e => setLibFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn btn-ghost" style={{ marginLeft: 'auto', height: 32 }} onClick={load}><RefreshCw size={13} /></button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{libItems.length} items</span>
        </div>

        {/* Library table */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : libItems.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            <Sparkles size={32} style={{ opacity: 0.2, marginBottom: 12 }} />
            <div>No content yet — generate your first clip above</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Name', 'Type', 'Voice', 'Status', 'Duration', 'Preview', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {libItems.map(item => {
                const cfg    = STATUS_CFG[item.status] || { label: item.status, color: '#6b7280' };
                const ct     = CONTENT_TYPES.find(c => c.id === item.content_type);
                const vName  = voices.find(v => v.id === item.voice_id)?.name || '—';
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', maxWidth: 220 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                      {item.input_text && <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>"{item.input_text}"</div>}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      {ct && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: ct.color }}>
                          <ct.icon size={12} /> {ct.label}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{vName}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                        background: cfg.color + '22', color: cfg.color,
                      }}>
                        {item.status === 'generating' && <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, animation: 'pulse 1s infinite' }} />}
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {item.duration_seconds ? `${Math.round(item.duration_seconds)}s` : '—'}
                    </td>
                    <td style={{ padding: '10px 16px', minWidth: 160 }}>
                      {(item.status === 'approved' || item.status === 'active' || item.asset_url) ? (
                        <MiniPlayer url={`${MEDIA}/${item.id}.mp3`} accentColor={ct?.color || '#2f81f7'} />
                      ) : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {item.status === 'pending_review' && (
                          <>
                            <button onClick={() => handleApprove(item.id)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#22c55e22', color: '#22c55e', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Approve</button>
                            <button onClick={() => handleReject(item.id)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#ef444418', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Reject</button>
                          </>
                        )}
                        {item.status === 'approved' && (
                          <button onClick={() => handleActivate(item.id)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: '#2f81f722', color: '#2f81f7', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Activate</button>
                        )}
                        {item.status === 'generating' && (
                          <button onClick={load} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: 'var(--bg-elevated)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}>Refresh</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
